import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { isObject, isSet } from "../../utils/commonUtils";
import FormInputs from "../../components/UI/FormInputs";
import WithRoutify from "../../hoc/WithRoutify";
import withReduxState from "../../hoc/wReduxState";
import Buttons from "../../components/UI/Buttons";
import OrganizationService from "../../services/OrganizationService";
import DataPermissionTemplateService from "../../services/DataPermissionTemplateService";
import DataFilters from "../RolesContainer/DataFilters";
import { isGlobalAdmin } from "../../utils/aclUtils";

const permissionConfigToDataFilter = (permissionConfig) => {
  if (permissionConfig == null || typeof permissionConfig !== "object") {
    return {};
  }
  if (
    permissionConfig.data_filter != null &&
    typeof permissionConfig.data_filter === "object"
  ) {
    return permissionConfig.data_filter;
  }
  return permissionConfig;
};

const formatApiValidationValue = (val) => {
  if (Array.isArray(val)) {
    return val.join(", ");
  }
  if (val != null && typeof val === "object") {
    return JSON.stringify(val);
  }
  return String(isSet(val, ""));
};

/** At least one screen added (accordion) with at least one filter row. */
const hasAtLeastOneDataFilterScreen = (df) => {
  if (!isObject(df) || Object.keys(df).length === 0) {
    return false;
  }
  return Object.values(df).some(
    (conditions) => Array.isArray(conditions) && conditions.length > 0
  );
};

const hasAnyDataFilterScreen = (df) =>
  isObject(df) && Object.keys(df).length > 0;

const AddDataPermissionTemplate = (props) => {
  const [validation, setValidation] = useState({});
  const [formData, setFormData] = useState({});
  const [dataFilter, setDataFilter] = useState({});
  const {
    isOpen,
    addFormHandler,
    fetchList,
    modelTitle,
    activeEditId,
    cloneSourceId,
    Toast,
    Loader,
    formDataInEdit,
    profile,
  } = props;

  const setValidationMessage = useCallback(
    ({ screenKey, index, fieldName, action = "clearField" }) => {
      const normalizedScreenKey = String(isSet(screenKey, "")).trim();
      if (normalizedScreenKey === "") {
        return;
      }

      const screenKeyVariants = [
        normalizedScreenKey,
        normalizedScreenKey.toLowerCase(),
      ];

      setValidation((prevValidation) => {
        const currentValidation = isSet(prevValidation, {});
        const nextValidation = { ...currentValidation };
        let didChange = false;

        const deleteKeysByPredicate = (predicate) => {
          Object.keys(nextValidation).forEach((key) => {
            if (predicate(key)) {
              delete nextValidation[key];
              didChange = true;
            }
          });
        };

        if (action === "clearScreen") {
          deleteKeysByPredicate((key) =>
            screenKeyVariants.some(
              (screenVariant) =>
                String(key).startsWith(`data_filter.${screenVariant}.`) ||
                String(key).startsWith(`${screenVariant}.`)
            )
          );
          return didChange ? nextValidation : prevValidation;
        }

        if (!Number.isFinite(Number(index))) {
          return prevValidation;
        }
        const idx = Number(index);

        if (action === "removeIndex") {
          const fieldAliases = [
            "field_key",
            "field",
            "operator",
            "value",
            "conditional",
            "condtional",
          ];
          const keyPattern =
            /^((?:data_filter\.)?)([^.]+)\.(\d+)\.(field_key|field|operator|value|conditional|condtional)$/;
          const rebuiltEntries = [];
          const consumedKeys = new Set();

          Object.entries(nextValidation).forEach(([key, val]) => {
            const match = String(key).match(keyPattern);
            if (!match) {
              return;
            }
            const keyPrefix = isSet(match[1], "");
            const keyScreen = isSet(match[2], "");
            const keyIndex = Number(match[3]);
            const keyField = isSet(match[4], "");
            const isSameScreen = screenKeyVariants.some(
              (screenVariant) =>
                String(keyScreen).toLowerCase() ===
                String(screenVariant).toLowerCase()
            );
            if (!isSameScreen || !fieldAliases.includes(keyField)) {
              return;
            }
            consumedKeys.add(key);
            if (keyIndex === idx) {
              didChange = true;
              return;
            }
            if (keyIndex > idx) {
              rebuiltEntries.push([
                `${keyPrefix}${keyScreen}.${keyIndex - 1}.${keyField}`,
                val,
              ]);
              didChange = true;
              return;
            }
            rebuiltEntries.push([key, val]);
          });

          if (didChange === true) {
            consumedKeys.forEach((key) => {
              delete nextValidation[key];
            });
            rebuiltEntries.forEach(([key, val]) => {
              nextValidation[key] = val;
            });
          }
          return didChange ? nextValidation : prevValidation;
        }

        const normalizedFieldName = String(isSet(fieldName, "")).trim();
        if (normalizedFieldName === "") {
          return prevValidation;
        }
        const fieldAliases = {
          field_key: ["field_key", "field"],
          operator: ["operator"],
          value: ["value"],
          conditional: ["conditional", "condtional"],
        };
        const keysToClear = isSet(fieldAliases[normalizedFieldName], [
          normalizedFieldName,
        ]);
        const candidateKeys = [];
        keysToClear.forEach((key) => {
          screenKeyVariants.forEach((screenVariant) => {
            candidateKeys.push(`data_filter.${screenVariant}.${idx}.${key}`);
            candidateKeys.push(`${screenVariant}.${idx}.${key}`);
          });
        });
        candidateKeys.forEach((candidate) => {
          if (Object.prototype.hasOwnProperty.call(nextValidation, candidate)) {
            delete nextValidation[candidate];
            didChange = true;
          }
        });
        return didChange ? nextValidation : prevValidation;
      });
    },
    []
  );

  useEffect(() => {
    if (activeEditId) {
      fetchRecord();
    } else if (cloneSourceId) {
      fetchCloneSource();
    } else {
      Loader(true);
      setFormData({
        ...formDataInEdit,
        is_active: isSet(formDataInEdit.is_active, 1),
      });
      setDataFilter(
        permissionConfigToDataFilter(formDataInEdit.permission_config)
      );
      setValidation({});
      Loader(false);
    }
  }, []);

  const fetchRecord = async () => {
    try {
      Loader(true);
      const res = await DataPermissionTemplateService.getById(activeEditId);
      if (res) {
        setFormData({
          ...res,
          is_active: res.is_active ? 1 : 0,
        });
        setDataFilter(permissionConfigToDataFilter(res.permission_config));
      }
      setValidation({});
      Loader(false);
    } catch (err) {
      Loader(false);
      console.log(err);
    }
  };

  const fetchCloneSource = async () => {
    try {
      Loader(true);
      const res = await DataPermissionTemplateService.getById(cloneSourceId);
      if (res) {
        const baseName = String(isSet(res.template_name, "")).trim();
        setFormData({
          ...res,
          data_permission_template_id: undefined,
          template_name: baseName ? `${baseName} (Copy)` : "Template (Copy)",
          is_active: res.is_active ? 1 : 0,
        });
        setDataFilter(permissionConfigToDataFilter(res.permission_config));
      }
      setValidation({});
      Loader(false);
    } catch (err) {
      Loader(false);
      console.log(err);
    }
  };

  const inputFields = [
    {
      type: "TextInput",
      label: "Template Name",
      placeholder: "Template Name",
      className: "form-control form-control-solid",
      name: "template_name",
      required: true,
    },
    {
      type: "TextArea",
      label: "Description",
      placeholder: "Optional description",
      className: "form-control form-control-solid",
      name: "description",
      required: false,
    },
    {
      type: "Checkbox",
      label: "Active",
      className: "form-check",
      name: "is_active",
      required: false,
    },
  ];

  const changeHandler = (val, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: val,
    }));
    setValidation((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const dataFilterChangeHandler = (next) => {
    const normalized = isSet(next, {});
    setDataFilter(normalized);
    if (hasAnyDataFilterScreen(normalized)) {
      setValidation((prev) => {
        if (!prev.screenSelect) {
          return prev;
        }
        const cleared = { ...prev };
        delete cleared.screenSelect;
        return cleared;
      });
    }
  };

  const formSubmitHandler = async () => {
    try {
      if (String(isSet(formData.template_name, "")).trim() === "") {
        setValidation((prev) => ({
          ...prev,
          template_name: "Template name is required",
        }));
        return;
      }

      if (!hasAtLeastOneDataFilterScreen(dataFilter)) {
        setValidation((prev) => ({
          ...prev,
          screenSelect:
            "Select a screen, click Add, and configure at least one filter row.",
        }));
        return;
      }

      const payload = {
        template_name: formData.template_name,
        description: formData.description || null,
        permission_config: dataFilter,
        is_active:
          formData.is_active === undefined || formData.is_active === null
            ? true
            : Boolean(Number(formData.is_active)),
      };

      let updateValidation = { ...validation };
      let data = null;
      if (activeEditId) {
        data = await DataPermissionTemplateService.updateTemplate(
          activeEditId,
          payload
        );
      } else {
        data = await DataPermissionTemplateService.createTemplate(payload);
      }

      if (data?.status && data?.status === "ERROR") {
        let hasDataFilterValidationError = false;
        if (isObject(data.data)) {
          for (const key of Object.keys(data.data)) {
            if (String(isSet(key, "")).startsWith("data_filter")) {
              hasDataFilterValidationError = true;
            }
            updateValidation = {
              ...updateValidation,
              [key]: formatApiValidationValue(data.data[key]),
            };
          }
          if (hasDataFilterValidationError) {
            updateValidation = {
              ...updateValidation,
              dataFiltersSummary:
                "Please correct Data Filters validation errors",
            };
          }
        } else {
          Toast.error(data.data);
        }
        if (Object.keys(updateValidation).length > 0) {
          setValidation(updateValidation);
        }
      } else if (data && (data.code === 200 || data.code === 201)) {
        fetchList();
        addFormHandler();
        Toast.success(
          `Template ${activeEditId ? "updated" : "added"} successfully`
        );
      } else {
        Toast.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      Toast.error("Something went wrong");
    }
  };

  const selectedOrganizationUuid = isSet(OrganizationService.getOrgId(), "");

  return (
    <>
      <Modal
        show={isOpen}
        onHide={addFormHandler}
        size="xl"
        dialogClassName="hResponsive"
        contentClassName="h-100"
        backdrop="static"
        keyboard={false}
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="font-weight-bold text-lg mb-0">{modelTitle}</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              {inputFields.map((field, index) => (
                <div className="col-12" key={index}>
                  <div className="form-group">
                    <label className="text-gray-900 text-md opacity-75">
                      {field.label}
                    </label>
                    <FormInputs
                      {...field}
                      changeHandler={(value, e) =>
                        changeHandler(value, field.name, e)
                      }
                      value={formData[field.name]}
                    />
                    {isSet(validation[field.name], "") !== "" ? (
                      <span className="text-danger m-1">
                        {isSet(validation[field.name], "")}
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <h6 className="text-gray-900 text-md font-weight-bold mb-2">
                Data access filters
              </h6>
              {isSet(validation.screenSelect, "") !== "" ? (
                <span className="text-danger m-1 d-block mb-2">
                  {validation.screenSelect}
                </span>
              ) : null}
              <DataFilters
                value={dataFilter}
                onChange={dataFilterChangeHandler}
                dataFilterValidationErrors={validation}
                isGlobalAdminUser={isGlobalAdmin(isSet(profile?.role, ""))}
                selectedOrganizationUuid={selectedOrganizationUuid}
                setValidationMessage={setValidationMessage}
              />
            </div>
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-center">
            <Buttons
              clickHandler={() => formSubmitHandler()}
              className="btn btn-primary"
              acl={"any"}
              label={activeEditId ? "Update" : "Save"}
            />
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default withReduxState(WithRoutify(AddDataPermissionTemplate));
